import { BrandBottomSheetModal } from "@/components/bottom-sheet-modal";
import { CreateRequestLayout } from "@/components/customer/CreateRequestLayout";
import { ThemedText } from "@/components/themed-text";
import { BinaryChoice } from "@/components/ui/binary-choice";
import { DropdownTrigger } from "@/components/ui/dropdown-trigger";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import {
  LOCATION_TYPES,
  MISFUEL_FUEL_TYPES,
  SPECIAL_ISSUES,
  TYRE_POSITIONS,
  WRONG_FUEL_LITRE_OPTIONS,
} from "@/data/vehicle";
import {
  BookingStep4Data,
  ServiceType,
  bookingStep4Schema,
} from "@/lib/validation/booking";
import { useCreateRequestStore } from "@/stores/use-create-request-store";
import { BottomSheetModal } from "@gorhom/bottom-sheet";
import { zodResolver } from "@hookform/resolvers/zod";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useRouter } from "expo-router";
import React, { useCallback, useMemo, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { StyleSheet, TextInput, View } from "react-native";
import { HelperText } from "react-native-paper";

export const VehiclePickUp = ({ route = true }: { route?: boolean }) => {
  const router = useRouter();
  const { theme } = useAuth();
  const desktop = useDesktopLayout();
  const questionLayout = desktop
    ? { style: styles.desktopQuestion, optionsStyle: styles.desktopQuestionOptions }
    : {};
  const { formData, setFormData, setCurrentStep } = useCreateRequestStore();
  const tyrePositionsSheetRef = useRef<BottomSheetModal>(null);
  const specialIssuesSheetRef = useRef<BottomSheetModal>(null);
  const requiredFuelSheetRef = useRef<BottomSheetModal>(null);
  const approxFuelLitresSheetRef = useRef<BottomSheetModal>(null);
  const wrongFuelSheetRef = useRef<BottomSheetModal>(null);
  const correctFuelSheetRef = useRef<BottomSheetModal>(null);
  const wrongFuelLitresSheetRef = useRef<BottomSheetModal>(null);
  const locationTypeSheetRef = useRef<BottomSheetModal>(null);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<BookingStep4Data>({
    resolver: zodResolver(bookingStep4Schema),
    defaultValues: {
      service_type: formData.service_type,
      questions: [
        {
          notes: formData.questions?.[0]?.notes,
          rolls_steers: formData.questions?.[0]?.rolls_steers,
          auto_neutral_roll: formData.questions?.[0]?.auto_neutral_roll,
          modified_low_clearance:
            formData.questions?.[0]?.modified_low_clearance,
          safe_location: formData.questions?.[0]?.safe_location,
          engine_starts: formData.questions?.[0]?.engine_starts,
          keys_available: formData.questions?.[0]?.keys_available,
          wheels_damaged: formData.questions?.[0]?.wheels_damaged,
          fluids_or_airbags: formData.questions?.[0]?.fluids_or_airbags,
          urgent_blocking: formData.questions?.[0]?.urgent_blocking,
          stuck_in: formData.questions?.[0]?.stuck_in,
          distance_band: formData.questions?.[0]?.distance_band,
          truck_access_nearby: formData.questions?.[0]?.truck_access_nearby,
          wheels_spinning: formData.questions?.[0]?.wheels_spinning,
          safe_to_work: formData.questions?.[0]?.safe_to_work,
          can_access_vehicle: formData.questions?.[0]?.can_access_vehicle,
          battery_location: formData.questions?.[0]?.battery_location,
          symptoms: formData.questions?.[0]?.symptoms,
          start_stop: formData.questions?.[0]?.start_stop,
          duration: formData.questions?.[0]?.duration,
          tyre_qty: formData.questions?.[0]?.tyre_qty,
          tyre_positions: formData.questions?.[0]?.tyre_positions,
          tyre_size: formData.questions?.[0]?.tyre_size,
          front_size_raw: formData.questions?.[0]?.front_size_raw,
          run_flat: formData.questions?.[0]?.run_flat,
          locking_nut: formData.questions?.[0]?.locking_nut,
          tyre_tier: formData.questions?.[0]?.tyre_tier,
          safe_access: formData.questions?.[0]?.safe_access,
          battery_type: formData.questions?.[0]?.battery_type,
          service_level: formData.questions?.[0]?.service_level,
          issue_type: formData.questions?.[0]?.issue_type,
          access_situation: formData.questions?.[0]?.access_situation,
          proof_of_ownership: formData.questions?.[0]?.proof_of_ownership,
          key_type: formData.questions?.[0]?.key_type,
          replacement_key: formData.questions?.[0]?.replacement_key,
          programming_needed: formData.questions?.[0]?.programming_needed,
          primary_concern: formData.questions?.[0]?.primary_concern,
          report_required: formData.questions?.[0]?.report_required,
          report_detail: formData.questions?.[0]?.report_detail,
          problem_category: formData.questions?.[0]?.problem_category,
          drivable: formData.questions?.[0]?.drivable,
          diagnosed_already: formData.questions?.[0]?.diagnosed_already,
          obd_codes: formData.questions?.[0]?.obd_codes,
          package: formData.questions?.[0]?.package,
          contamination: formData.questions?.[0]?.contamination,
          special_issues: formData.questions?.[0]?.special_issues,
          required_fuel_type: formData.questions?.[0]?.required_fuel_type,
          approx_fuel_litres: formData.questions?.[0]?.approx_fuel_litres,
          safe_position_confirmed:
            formData.questions?.[0]?.safe_position_confirmed,
          wrong_fuel_type: formData.questions?.[0]?.wrong_fuel_type,
          correct_fuel_type: formData.questions?.[0]?.correct_fuel_type,
          approx_wrong_fuel_litres:
            formData.questions?.[0]?.approx_wrong_fuel_litres,
          ignition_switched_on: formData.questions?.[0]?.ignition_switched_on,
          engine_started_after_misfuel:
            formData.questions?.[0]?.engine_started_after_misfuel,
          vehicle_driven_after_misfuel:
            formData.questions?.[0]?.vehicle_driven_after_misfuel,
          location_type: formData.questions?.[0]?.location_type,
        },
      ],
    },
  });

  const requiredFuelType = watch("questions.0.required_fuel_type");
  const approxFuelLitres = watch("questions.0.approx_fuel_litres");
  const wrongFuelType = watch("questions.0.wrong_fuel_type");
  const correctFuelType = watch("questions.0.correct_fuel_type");
  const approxWrongFuelLitres = watch("questions.0.approx_wrong_fuel_litres");
  const locationType = watch("questions.0.location_type");

  const handleTyrePositionSelect = useCallback(
    (selectedValue: string) => {
      const currentPositions =
        (watch("questions.0.tyre_positions") as string[]) || [];
      const mainPositions = TYRE_POSITIONS.filter((t) => t.value !== "all").map(
        (t) => t.value,
      );

      let newPositions: string[] = [];
      if (selectedValue === "all") {
        const allSelected = mainPositions.every((pos) =>
          currentPositions.includes(pos),
        );
        newPositions = allSelected ? [] : [...mainPositions];
      } else {
        if (currentPositions.includes(selectedValue)) {
          newPositions = currentPositions.filter((p) => p !== selectedValue);
        } else {
          newPositions = [...currentPositions, selectedValue];
        }
      }
      setValue("questions.0.tyre_positions", newPositions as any);
    },
    [watch, setValue],
  );

  const handleSpecialIssuesSelect = useCallback(
    (selectedValue: string) => {
      const currentIssues =
        (watch("questions.0.special_issues") as string[]) || [];

      let newIssues: string[] = [];
      if (currentIssues.includes(selectedValue)) {
        newIssues = currentIssues.filter((issue) => issue !== selectedValue);
      } else {
        newIssues = [...currentIssues, selectedValue];
      }
      setValue("questions.0.special_issues", newIssues as any);
    },
    [watch, setValue],
  );

  const effectiveSelectedValues = useMemo(() => {
    const current = (watch("questions.0.tyre_positions") as string[]) || [];
    const mainPositions = TYRE_POSITIONS.filter((t) => t.value !== "all").map(
      (t) => t.value,
    );
    const allSelected =
      mainPositions.length > 0 &&
      mainPositions.every((pos) => current.includes(pos));
    return allSelected ? [...current, "all"] : current;
  }, [watch("questions.0.tyre_positions")]);

  const effectiveSpecialIssues = useMemo(() => {
    const current = (watch("questions.0.special_issues") as string[]) || [];
    const mainIssues = SPECIAL_ISSUES.filter((t) => t.value !== "all").map(
      (t) => t.value,
    );
    const allSelected =
      mainIssues.length > 0 &&
      mainIssues.every((issue) => current.includes(issue));
    return allSelected ? [...mainIssues] : current;
  }, [watch("questions.0.special_issues")]);

  const onSubmit = (data: BookingStep4Data) => {
    setFormData(data);
    setCurrentStep(5);
    if (route) {
      router.push("/customer/create-request/summary");
    }
  };

  return (
    <CreateRequestLayout
      step={4}
      title="Vehicle Collection Information"
      subtitle="Please answer the following questions to help us provide you with the best possible service"
      onNext={handleSubmit(onSubmit)}
      onBack={() => (route ? router.back() : setCurrentStep(3))}
    >
      {/* ─── VEHICLE TRANSPORTATION ─── */}
      {formData.service_type == ServiceType.VEHICLE_TRANSPORTATION && (
        <>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.rolls_steers"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Can the vehicle roll/steer?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.rolls_steers && (
              <HelperText type="error">
                {errors.questions?.[0]?.rolls_steers?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.auto_neutral_roll"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Transmission-specific: if automatic, will it go into neutral and roll?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.auto_neutral_roll && (
              <HelperText type="error">
                {errors.questions?.[0]?.auto_neutral_roll?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.modified_low_clearance"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Low clearance / modifications?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.modified_low_clearance && (
              <HelperText type="error">
                {errors.questions?.[0]?.modified_low_clearance?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.keys_available"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Keys available at pickup?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.keys_available && (
              <HelperText type="error">
                {errors.questions?.[0]?.keys_available?.message}
              </HelperText>
            )}
          </View>
        </>
      )}

      {/* ─── BREAKDOWN RECOVERY ─── */}
      {formData.service_type == ServiceType.BREAKDOWN_RECOVERY && (
        <>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.rolls_steers"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Can the vehicle roll/steer?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.rolls_steers && (
              <HelperText type="error">
                {errors.questions?.[0]?.rolls_steers?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.safe_location"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Is the vehicle in a safe location?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.safe_location && (
              <HelperText type="error">
                {errors.questions?.[0]?.safe_location?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.engine_starts"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Can the engine start?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.engine_starts && (
              <HelperText type="error">
                {errors.questions?.[0]?.engine_starts?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.keys_available"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Keys available at pickup?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.keys_available && (
              <HelperText type="error">
                {errors.questions?.[0]?.keys_available?.message}
              </HelperText>
            )}
          </View>
        </>
      )}

      {/* ─── ACCIDENT RECOVERY ─── */}
      {formData.service_type == ServiceType.ACCIDENT_RECOVERY && (
        <>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.rolls_steers"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Can the vehicle roll/steer?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.rolls_steers && (
              <HelperText type="error">
                {errors.questions?.[0]?.rolls_steers?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.auto_neutral_roll"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Transmission-specific: if automatic, will it go into neutral and roll?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.auto_neutral_roll && (
              <HelperText type="error">
                {errors.questions?.[0]?.auto_neutral_roll?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.wheels_damaged"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Are any wheels damaged / not rotating?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.wheels_damaged && (
              <HelperText type="error">
                {errors.questions?.[0]?.wheels_damaged?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.fluids_or_airbags"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Fluid leakage / airbags deployed?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.fluids_or_airbags && (
              <HelperText type="error">
                {errors.questions?.[0]?.fluids_or_airbags?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.urgent_blocking"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Blocking road / urgent?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.urgent_blocking && (
              <HelperText type="error">
                {errors.questions?.[0]?.urgent_blocking?.message}
              </HelperText>
            )}
          </View>
        </>
      )}

      {/* ─── PULL OUT ─── */}
      {formData.service_type == ServiceType.PULL_OUT && (
        <>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.stuck_in"
              render={({ field: { value, onChange } }) => (
                <>
                  <Label label="Stuck in mud, snow, ditch, etc?" />
                  <Input
                    placeholder="Describe where the vehicle is stuck"
                    value={value}
                    onChangeText={onChange}
                  />
                </>
              )}
            />
            {errors.questions?.[0]?.stuck_in && (
              <HelperText type="error">
                {errors.questions?.[0]?.stuck_in?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.distance_band"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Is the vehicle more than 5m off road?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.distance_band && (
              <HelperText type="error">
                {errors.questions?.[0]?.distance_band?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.truck_access_nearby"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Can a recovery truck access within 5–10m?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.truck_access_nearby && (
              <HelperText type="error">
                {errors.questions?.[0]?.truck_access_nearby?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.wheels_spinning"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Are wheels spinning freely?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.wheels_spinning && (
              <HelperText type="error">
                {errors.questions?.[0]?.wheels_spinning?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.safe_to_work"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Is it safe to work at the location?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.safe_to_work && (
              <HelperText type="error">
                {errors.questions?.[0]?.safe_to_work?.message}
              </HelperText>
            )}
          </View>
        </>
      )}

      {/* ─── OUT OF FUEL ─── */}
      {formData.service_type == ServiceType.OUT_OF_FUEL && (
        <>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.required_fuel_type"
              render={({ field: { value } }) => (
                <DropdownTrigger
                  label="Fuel required"
                  value={
                    MISFUEL_FUEL_TYPES.find((option) => option.value === value)
                      ?.label ?? value
                  }
                  placeholder="Select fuel"
                  onPress={() => requiredFuelSheetRef.current?.present()}
                  hasError={!!errors.questions?.[0]?.required_fuel_type}
                />
              )}
            />
            {errors.questions?.[0]?.required_fuel_type && (
              <HelperText type="error">
                {errors.questions?.[0]?.required_fuel_type?.message}
              </HelperText>
            )}
          </View>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.approx_fuel_litres"
              render={({ field: { value } }) => (
                <DropdownTrigger
                  label="Approximate litres needed"
                  value={
                    value == null
                      ? undefined
                      : (WRONG_FUEL_LITRE_OPTIONS.find(
                          (option) => option.value === String(value),
                        )?.label ?? `${value} litres`)
                  }
                  placeholder="Select amount"
                  onPress={() => approxFuelLitresSheetRef.current?.present()}
                  hasError={!!errors.questions?.[0]?.approx_fuel_litres}
                />
              )}
            />
            {errors.questions?.[0]?.approx_fuel_litres && (
              <HelperText type="error">
                {errors.questions?.[0]?.approx_fuel_litres?.message}
              </HelperText>
            )}
          </View>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.safe_position_confirmed"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Vehicle is in a safe, legal position for fuel delivery"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
          </View>
        </>
      )}

      {/* ─── MISFUEL ─── */}
      {formData.service_type == ServiceType.MISFUEL && (
        <>
          <View style={[styles.section, styles.warningBanner]}>
            <ThemedText style={styles.warningText}>
              Do not start the engine. A specialist will drain and flush the
              fuel system.
            </ThemedText>
          </View>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.wrong_fuel_type"
              render={({ field: { value } }) => (
                <DropdownTrigger
                  label="Wrong fuel added"
                  value={
                    MISFUEL_FUEL_TYPES.find((option) => option.value === value)
                      ?.label ?? value
                  }
                  placeholder="Select fuel"
                  onPress={() => wrongFuelSheetRef.current?.present()}
                  hasError={!!errors.questions?.[0]?.wrong_fuel_type}
                />
              )}
            />
            {errors.questions?.[0]?.wrong_fuel_type && (
              <HelperText type="error">
                {errors.questions?.[0]?.wrong_fuel_type?.message}
              </HelperText>
            )}
          </View>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.correct_fuel_type"
              render={({ field: { value } }) => (
                <DropdownTrigger
                  label="Correct fuel for vehicle"
                  value={
                    MISFUEL_FUEL_TYPES.find((option) => option.value === value)
                      ?.label ?? value
                  }
                  placeholder="Select fuel"
                  onPress={() => correctFuelSheetRef.current?.present()}
                  hasError={!!errors.questions?.[0]?.correct_fuel_type}
                />
              )}
            />
            {errors.questions?.[0]?.correct_fuel_type && (
              <HelperText type="error">
                {errors.questions?.[0]?.correct_fuel_type?.message}
              </HelperText>
            )}
          </View>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.approx_wrong_fuel_litres"
              render={({ field: { value } }) => (
                <DropdownTrigger
                  label="Approximate amount put in (litres)"
                  value={
                    value == null
                      ? undefined
                      : (WRONG_FUEL_LITRE_OPTIONS.find(
                          (option) => option.value === String(value),
                        )?.label ?? `${value} litres`)
                  }
                  placeholder="Select amount"
                  onPress={() => wrongFuelLitresSheetRef.current?.present()}
                  hasError={!!errors.questions?.[0]?.approx_wrong_fuel_litres}
                />
              )}
            />
            {errors.questions?.[0]?.approx_wrong_fuel_litres && (
              <HelperText type="error">
                {errors.questions?.[0]?.approx_wrong_fuel_litres?.message}
              </HelperText>
            )}
          </View>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.location_type"
              render={({ field: { value } }) => (
                <DropdownTrigger
                  label="Location type"
                  value={
                    LOCATION_TYPES.find((option) => option.value === value)
                      ?.label ?? value
                  }
                  placeholder="Select location type"
                  onPress={() => locationTypeSheetRef.current?.present()}
                  hasError={!!errors.questions?.[0]?.location_type}
                />
              )}
            />
            {errors.questions?.[0]?.location_type && (
              <HelperText type="error">
                {errors.questions?.[0]?.location_type?.message}
              </HelperText>
            )}
          </View>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.ignition_switched_on"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Was the ignition switched on?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.ignition_switched_on && (
              <HelperText type="error">
                {errors.questions?.[0]?.ignition_switched_on?.message}
              </HelperText>
            )}
          </View>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.engine_started_after_misfuel"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Was the engine started after misfuel?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.engine_started_after_misfuel && (
              <HelperText type="error">
                {errors.questions?.[0]?.engine_started_after_misfuel?.message}
              </HelperText>
            )}
          </View>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.vehicle_driven_after_misfuel"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Was the vehicle driven after misfuel?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.vehicle_driven_after_misfuel && (
              <HelperText type="error">
                {errors.questions?.[0]?.vehicle_driven_after_misfuel?.message}
              </HelperText>
            )}
          </View>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.safe_position_confirmed"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Vehicle is in a safe position for specialist access"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.safe_position_confirmed && (
              <HelperText type="error">
                {errors.questions?.[0]?.safe_position_confirmed?.message}
              </HelperText>
            )}
          </View>
        </>
      )}

      {/* ─── JUMP START ─── */}
      {formData.service_type === ServiceType.JUMP_START && (
        <>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.can_access_vehicle"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Can you access the vehicle (unlocked)?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.can_access_vehicle && (
              <HelperText type="error">
                {errors.questions?.[0]?.can_access_vehicle?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.battery_location"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Is battery accessible under the bonnet?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.battery_location && (
              <HelperText type="error">
                {errors.questions?.[0]?.battery_location?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.symptoms"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Any visible symptoms (smoke, leaks, warning lights)?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.symptoms && (
              <HelperText type="error">
                {errors.questions?.[0]?.symptoms?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.start_stop"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Is the vehicle start-stop?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.start_stop && (
              <HelperText type="error">
                {errors.questions?.[0]?.start_stop?.message}
              </HelperText>
            )}
          </View>
        </>
      )}

      {/* ─── VEHICLE STORAGE ─── */}
      {formData.service_type === ServiceType.VEHICLE_STORAGE && (
        <>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.rolls_steers"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Can the vehicle roll/steer?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.rolls_steers && (
              <HelperText type="error">
                {errors.questions?.[0]?.rolls_steers?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.keys_available"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Keys available at pickup?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.keys_available && (
              <HelperText type="error">
                {errors.questions?.[0]?.keys_available?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Label label="Storage Duration" />
            <Controller
              control={control}
              name="questions.0.duration"
              render={({ field: { value, onChange } }) => (
                <View
                  style={[
                    styles.inputRow,
                    {
                      backgroundColor: theme.background,
                      borderColor: theme.border,
                    },
                  ]}
                >
                  <Ionicons
                    name="calendar-outline"
                    size={20}
                    color={theme.text}
                    style={styles.storageInputIcon}
                  />
                  <TextInput
                    placeholder="e.g. 1, 2, 4"
                    placeholderTextColor={theme.text}
                    keyboardType="numeric"
                    value={value?.toString() ?? ""}
                    onChangeText={(v) => onChange(String(v))}
                    style={[styles.storageInput, { color: theme.text }]}
                  />
                  <ThemedText style={styles.storageInputSuffix}>
                    Days
                  </ThemedText>
                </View>
              )}
            />
            {errors.questions?.[0]?.duration && (
              <HelperText type="error">
                {errors.questions?.[0]?.duration?.message}
              </HelperText>
            )}
          </View>
        </>
      )}

      {/* ─── MOBILE TYRE FITTING ─── */}
      {formData.service_type == ServiceType.MOBILE_TYRE_FITTING && (
        <>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.tyre_qty"
              render={({ field: { value, onChange } }) => (
                <>
                  <Label label="How many tyres need fitting?" />
                  <Input
                    placeholder="e.g. 1, 2, 4"
                    keyboardType="numeric"
                    value={value?.toString()}
                    onChangeText={(v) => onChange(parseInt(v) || 0)}
                  />
                </>
              )}
            />
            {errors.questions?.[0]?.tyre_qty && (
              <HelperText type="error">
                {errors.questions?.[0]?.tyre_qty?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.tyre_size"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Do you know your tyre size?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.tyre_size && (
              <HelperText type="error">
                {errors.questions?.[0]?.tyre_size?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.tyre_positions"
              render={({ field: { value } }) => (
                <DropdownTrigger
                  label="Tyre Positions"
                  value={(value as string[])
                    ?.map(
                      (v) => TYRE_POSITIONS.find((t) => t.value === v)?.label,
                    )
                    .join(", ")}
                  placeholder="Select tyre positions"
                  onPress={() => tyrePositionsSheetRef.current?.present()}
                  hasError={!!errors.questions?.[0]?.tyre_positions}
                />
              )}
            />
            {errors.questions?.[0]?.tyre_positions && (
              <HelperText type="error">
                {errors.questions?.[0]?.tyre_positions?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.front_size_raw"
              render={({ field: { value, onChange } }) => (
                <>
                  <Label label="Tyre size (e.g. 205/55 R16)" />
                  <Input
                    placeholder="Enter tyre size"
                    value={value}
                    onChangeText={onChange}
                  />
                </>
              )}
            />
            {errors.questions?.[0]?.front_size_raw && (
              <HelperText type="error">
                {errors.questions?.[0]?.front_size_raw?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.run_flat"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Are run-flat tyres required?"
                  value={value}
                  onValueChange={onChange}
                  notSureOption={true}
                />
              )}
            />
            {errors.questions?.[0]?.run_flat && (
              <HelperText type="error">
                {errors.questions?.[0]?.run_flat?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.locking_nut"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Is there a locking wheel nut? (key available?)"
                  value={value}
                  onValueChange={onChange}
                  notSureOption={true}
                />
              )}
            />
            {errors.questions?.[0]?.locking_nut && (
              <HelperText type="error">
                {errors.questions?.[0]?.locking_nut?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.start_stop"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Is the vehicle start-stop?"
                  value={value}
                  onValueChange={onChange}
                  notSureOption={true}
                />
              )}
            />
            {errors.questions?.[0]?.start_stop && (
              <HelperText type="error">
                {errors.questions?.[0]?.start_stop?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.tyre_tier"
              render={({ field: { value, onChange } }) => (
                <>
                  <Label label="Tyre tier preference" />
                  <Input
                    placeholder="Budget / Mid-range / Premium"
                    value={value}
                    onChangeText={onChange}
                  />
                </>
              )}
            />
            {errors.questions?.[0]?.tyre_tier && (
              <HelperText type="error">
                {errors.questions?.[0]?.tyre_tier?.message}
              </HelperText>
            )}
          </View>
        </>
      )}

      {/* ─── MOBILE BATTERY FITTING ─── */}
      {formData.service_type == ServiceType.MOBILE_BATTERY_FITTING && (
        <>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.battery_type"
              render={({ field: { value, onChange } }) => (
                <>
                  <Label label="Battery type (if known)" />
                  <Input
                    placeholder="e.g. AGM, EFB, Standard"
                    value={value}
                    onChangeText={onChange}
                  />
                </>
              )}
            />
            {errors.questions?.[0]?.battery_type && (
              <HelperText type="error">
                {errors.questions?.[0]?.battery_type?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.service_level"
              render={({ field: { value, onChange } }) => (
                <>
                  <Label label="Service level preference" />
                  <Input
                    placeholder="Standard / Premium"
                    value={value}
                    onChangeText={onChange}
                  />
                </>
              )}
            />
            {errors.questions?.[0]?.service_level && (
              <HelperText type="error">
                {errors.questions?.[0]?.service_level?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.tyre_tier"
              render={({ field: { value, onChange } }) => (
                <>
                  <Label label="Brand tier preference" />
                  <Input
                    placeholder="Budget / Mid-range / Premium"
                    value={value}
                    onChangeText={onChange}
                  />
                </>
              )}
            />
            {errors.questions?.[0]?.tyre_tier && (
              <HelperText type="error">
                {errors.questions?.[0]?.tyre_tier?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.safe_access"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Is the vehicle in a safe / accessible location?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.safe_access && (
              <HelperText type="error">
                {errors.questions?.[0]?.safe_access?.message}
              </HelperText>
            )}
          </View>
        </>
      )}

      {/* ─── AUTO LOCKSMITH ─── */}
      {formData.service_type == ServiceType.AUTO_LOCKSMITH && (
        <>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.issue_type"
              render={({ field: { value, onChange } }) => (
                <>
                  <Label label="What is the locksmith issue?" />
                  <Input
                    placeholder="e.g. Locked out, broken key, lost key"
                    value={value}
                    onChangeText={onChange}
                  />
                </>
              )}
            />
            {errors.questions?.[0]?.issue_type && (
              <HelperText type="error">
                {errors.questions?.[0]?.issue_type?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.access_situation"
              render={({ field: { value, onChange } }) => (
                <>
                  <Label label="Describe the access situation" />
                  <Input
                    placeholder="e.g. key snapped in lock, key fob not working"
                    value={value}
                    onChangeText={onChange}
                  />
                </>
              )}
            />
            {errors.questions?.[0]?.access_situation && (
              <HelperText type="error">
                {errors.questions?.[0]?.access_situation?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.proof_of_ownership"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Can you provide proof of vehicle ownership?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.proof_of_ownership && (
              <HelperText type="error">
                {errors.questions?.[0]?.proof_of_ownership?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.key_type"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Is the key a smart / transponder key?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.key_type && (
              <HelperText type="error">
                {errors.questions?.[0]?.key_type?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.replacement_key"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Is a replacement key required?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.replacement_key && (
              <HelperText type="error">
                {errors.questions?.[0]?.replacement_key?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.programming_needed"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Does the key need programming / coding?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.programming_needed && (
              <HelperText type="error">
                {errors.questions?.[0]?.programming_needed?.message}
              </HelperText>
            )}
          </View>
        </>
      )}

      {/* ─── VEHICLE DIAGNOSTICS ─── */}
      {formData.service_type == ServiceType.VEHICLE_DIAGNOSTICS && (
        <>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.primary_concern"
              render={({ field: { value, onChange } }) => (
                <>
                  <Label label="What is your primary concern?" />
                  <Input
                    placeholder="e.g. warning light, poor performance"
                    value={value}
                    onChangeText={onChange}
                  />
                </>
              )}
            />
            {errors.questions?.[0]?.primary_concern && (
              <HelperText type="error">
                {errors.questions?.[0]?.primary_concern?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.report_required"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Is a written diagnostic report required?"
                  value={value}
                  onValueChange={(val) => {
                    onChange(val);
                    if (!val) {
                      setValue("questions.0.report_detail", undefined);
                    }
                  }}
                />
              )}
            />
            {errors.questions?.[0]?.report_required && (
              <HelperText type="error">
                {errors.questions?.[0]?.report_required?.message}
              </HelperText>
            )}
          </View>

          {watch("questions.0.report_required") && (
            <View style={styles.section}>
              <Label label="Please provide details for the diagnostic report" />
              <Controller
                control={control}
                name="questions.0.report_detail"
                render={({ field: { value, onChange } }) => (
                  <Input
                    value={value}
                    onChangeText={onChange}
                    multiline
                    numberOfLines={4}
                  />
                )}
              />
              {errors.questions?.[0]?.report_detail && (
                <HelperText type="error">
                  {errors.questions?.[0]?.report_detail?.message}
                </HelperText>
              )}
            </View>
          )}

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.safe_access"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Is the vehicle in a safe / accessible location?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.safe_access && (
              <HelperText type="error">
                {errors.questions?.[0]?.safe_access?.message}
              </HelperText>
            )}
          </View>
        </>
      )}

      {/* ─── MOBILE MECHANICS ─── */}
      {formData.service_type == ServiceType.MOBILE_MECHANICS && (
        <>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.problem_category"
              render={({ field: { value, onChange } }) => (
                <>
                  <Label label="What category best describes the problem?" />
                  <Input
                    placeholder="e.g. Engine, Brakes, Suspension, Electrical"
                    value={value}
                    onChangeText={onChange}
                  />
                </>
              )}
            />
            {errors.questions?.[0]?.problem_category && (
              <HelperText type="error">
                {errors.questions?.[0]?.problem_category?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.drivable"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Is the vehicle currently drivable?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.drivable && (
              <HelperText type="error">
                {errors.questions?.[0]?.drivable?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.diagnosed_already"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Has the vehicle been diagnosed elsewhere?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.diagnosed_already && (
              <HelperText type="error">
                {errors.questions?.[0]?.diagnosed_already?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.obd_codes"
              render={({ field: { value, onChange } }) => (
                <BinaryChoice
                  {...questionLayout}
                  label="Do you have any OBD fault codes?"
                  value={value}
                  onValueChange={onChange}
                />
              )}
            />
            {errors.questions?.[0]?.obd_codes && (
              <HelperText type="error">
                {errors.questions?.[0]?.obd_codes?.message}
              </HelperText>
            )}
          </View>
        </>
      )}

      {/* ─── MOBILE VALET ─── */}
      {formData.service_type == ServiceType.MOBILE_VALET && (
        <>
          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.package"
              render={({ field: { value, onChange } }) => (
                <>
                  <Label label="Which valet package do you require?" />
                  <Input
                    placeholder="e.g. Full, Mini, Interior, Exterior"
                    value={value}
                    onChangeText={onChange}
                  />
                </>
              )}
            />
            {errors.questions?.[0]?.package && (
              <HelperText type="error">
                {errors.questions?.[0]?.package?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.contamination"
              render={({ field: { value, onChange } }) => (
                <>
                  <Label label="Any heavy contamination? (pet hair, smoke, stains)" />
                  <Input
                    placeholder="Describe any heavy soiling"
                    value={value}
                    onChangeText={onChange}
                  />
                </>
              )}
            />
            {errors.questions?.[0]?.contamination && (
              <HelperText type="error">
                {errors.questions?.[0]?.contamination?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Controller
              control={control}
              name="questions.0.special_issues"
              render={({ field: { value, onChange } }) => (
                <DropdownTrigger
                  label="Special issues"
                  value={(value as string[])
                    ?.map(
                      (v) => SPECIAL_ISSUES.find((t) => t.value === v)?.label,
                    )
                    .join(", ")}
                  placeholder="Select special issues"
                  onPress={() => specialIssuesSheetRef.current?.present()}
                  hasError={!!errors.questions?.[0]?.special_issues}
                />
              )}
            />
            {errors.questions?.[0]?.special_issues && (
              <HelperText type="error">
                {errors.questions?.[0]?.special_issues?.message}
              </HelperText>
            )}
          </View>
        </>
      )}

      {/* ─── NOTES (all service types) ─── */}
      <View style={styles.section}>
        <Label label={`Anything the operator should know? (Optional)`} required={false} />
        <Controller
          control={control}
          name="questions.0.notes"
          render={({ field: { value, onChange } }) => (
            <View style={styles.textAreaContainer}>
              <TextInput
                style={[
                  styles.textArea,
                  {
                    backgroundColor: theme.background,
                    borderColor: theme.border,
                    color: theme.text,
                  },
                ]}
                placeholder="Provide additional details like contact information, accessibility notes, or specific vehicle information to assist the operator."
                placeholderTextColor={BrandColors.mediumGray}
                value={value ?? ""}
                onChangeText={onChange}
                multiline
                numberOfLines={4}
                textAlignVertical="top"
                maxLength={500}
              />
              <ThemedText style={styles.characterCount} type="extraSmall">
                {(value || "").length}/500
              </ThemedText>
            </View>
          )}
        />
        {errors.questions?.[0]?.notes && (
          <HelperText type="error">
            {errors.questions?.[0]?.notes?.message}
          </HelperText>
        )}
      </View>
      <BrandBottomSheetModal
        ref={tyrePositionsSheetRef}
        title="Select Tyre Positions"
        subtitle="Which tyres need fitting?"
        options={TYRE_POSITIONS}
        multiple={true}
        selectedValues={effectiveSelectedValues}
        onSelect={handleTyrePositionSelect}
        onClose={() => tyrePositionsSheetRef.current?.dismiss()}
      />
      <BrandBottomSheetModal
        ref={specialIssuesSheetRef}
        title="Select Special Issues"
        subtitle="Which special issues need to be addressed?"
        options={SPECIAL_ISSUES}
        multiple={true}
        selectedValues={effectiveSpecialIssues}
        onSelect={handleSpecialIssuesSelect}
        onClose={() => specialIssuesSheetRef.current?.dismiss()}
      />
      <BrandBottomSheetModal
        ref={requiredFuelSheetRef}
        title="Fuel required"
        subtitle="Select the fuel to deliver"
        options={MISFUEL_FUEL_TYPES}
        selectedValue={requiredFuelType}
        onSelect={(value) => {
          setValue("questions.0.required_fuel_type", value, {
            shouldValidate: true,
          });
        }}
        onClose={() => requiredFuelSheetRef.current?.dismiss()}
      />
      <BrandBottomSheetModal
        ref={approxFuelLitresSheetRef}
        title="Approximate litres needed"
        subtitle="Select the approximate litres"
        options={WRONG_FUEL_LITRE_OPTIONS}
        selectedValue={
          approxFuelLitres != null ? String(approxFuelLitres) : undefined
        }
        onSelect={(value) => {
          setValue("questions.0.approx_fuel_litres", Number(value), {
            shouldValidate: true,
          });
        }}
        onClose={() => approxFuelLitresSheetRef.current?.dismiss()}
      />
      <BrandBottomSheetModal
        ref={wrongFuelSheetRef}
        title="Wrong fuel added"
        subtitle="Select the fuel that was put in"
        options={MISFUEL_FUEL_TYPES}
        selectedValue={wrongFuelType}
        onSelect={(value) => {
          setValue("questions.0.wrong_fuel_type", value, {
            shouldValidate: true,
          });
        }}
        onClose={() => wrongFuelSheetRef.current?.dismiss()}
      />
      <BrandBottomSheetModal
        ref={correctFuelSheetRef}
        title="Correct fuel for vehicle"
        subtitle="Select the fuel the vehicle uses"
        options={MISFUEL_FUEL_TYPES}
        selectedValue={correctFuelType}
        onSelect={(value) => {
          setValue("questions.0.correct_fuel_type", value, {
            shouldValidate: true,
          });
        }}
        onClose={() => correctFuelSheetRef.current?.dismiss()}
      />
      <BrandBottomSheetModal
        ref={wrongFuelLitresSheetRef}
        title="Approximate amount put in"
        subtitle="Select the approximate litres"
        options={WRONG_FUEL_LITRE_OPTIONS}
        selectedValue={
          approxWrongFuelLitres != null
            ? String(approxWrongFuelLitres)
            : undefined
        }
        onSelect={(value) => {
          setValue("questions.0.approx_wrong_fuel_litres", Number(value), {
            shouldValidate: true,
          });
        }}
        onClose={() => wrongFuelLitresSheetRef.current?.dismiss()}
      />
      <BrandBottomSheetModal
        ref={locationTypeSheetRef}
        title="Location type"
        subtitle="Select where the vehicle is"
        options={LOCATION_TYPES}
        selectedValue={locationType}
        onSelect={(value) => {
          setValue("questions.0.location_type", value, {
            shouldValidate: true,
          });
        }}
        onClose={() => locationTypeSheetRef.current?.dismiss()}
      />
    </CreateRequestLayout>
  );
};

const styles = StyleSheet.create({
  section: {
    marginBottom: 12,
    gap: 8,
  },
  desktopQuestion: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 24,
  },
  desktopQuestionOptions: {
    minWidth: 400,
  },
  helperText: {
    fontSize: 12,
    color: "#64748b",
    marginTop: 8,
  },
  textAreaContainer: {
    position: "relative",
  },
  textArea: {
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    minHeight: 120,
    borderWidth: 1,
  },
  characterCount: {
    position: "absolute",
    bottom: 8,
    right: 12,
    fontSize: 12,
    color: "#64748b",
  },
  inputRow: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 16,
    height: 50,
  },
  storageInputIcon: {
    marginRight: 12,
  },
  storageInput: {
    flex: 1,
    fontSize: 16,
    paddingVertical: 0,
  },
  storageInputSuffix: {
    marginLeft: 8,
    fontSize: 16,
    fontWeight: "500",
  },
  warningBanner: {
    backgroundColor: "#FEF3C7",
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: "#F59E0B",
  },
  warningText: {
    color: "#92400E",
    fontWeight: "600",
  },
});
